import {
  Check,
  Compass,
  Facebook,
  FileBarChart,
  Instagram,
  Rocket,
  Search,
  SearchCheck,
  SlidersHorizontal,
  Youtube,
} from "lucide-react";
import { Seo } from "../components/Seo";
import { Reveal } from "../components/Reveal";
import { CTAButton } from "../components/CTAButton";
import { CTABanner } from "../components/CTABanner";

const metaAds = [
  "Campaign strategy and funnel planning",
  "Audience research and targeting",
  "Ad creative and copy direction",
  "Lead generation and sales campaigns",
  "Retargeting and lookalike audiences",
  "Pixel and conversion tracking setup",
  "Weekly optimisation and reporting",
];

const googleAds = [
  "Search campaigns for high-intent customers",
  "Display and YouTube advertising",
  "Performance Max campaigns",
  "Keyword research and negative keyword management",
  "Ad copy and landing page recommendations",
  "Conversion tracking and analytics setup",
  "Weekly optimisation and reporting",
];

const process = [
  { icon: SearchCheck, title: "Audit" },
  { icon: Compass, title: "Strategy" },
  { icon: Rocket, title: "Launch" },
  { icon: SlidersHorizontal, title: "Optimise" },
  { icon: FileBarChart, title: "Report" },
];

const ServiceBlock = ({ icons, title, intro, bullets, cta, testid, ctaTestid, flip }) => (
  <div className={`grid gap-10 lg:grid-cols-2 lg:gap-16 items-start ${flip ? "" : ""}`} data-testid={testid}>
    <div className={flip ? "lg:order-2" : ""}>
      <Reveal>
        <div className="flex gap-3">
          {icons.map((Icon, i) => (
            <span key={i} className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-brand-50 text-brand-600">
              <Icon className="h-5 w-5" />
            </span>
          ))}
        </div>
      </Reveal>
      <Reveal delay={0.1}>
        <h2 className="mt-6 font-display text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">{title}</h2>
      </Reveal>
      <Reveal delay={0.2}>
        <p className="mt-4 text-base md:text-lg leading-relaxed text-slate-600">{intro}</p>
      </Reveal>
      <Reveal delay={0.3}>
        <div className="mt-8">
          <CTAButton testid={ctaTestid}>{cta}</CTAButton>
        </div>
      </Reveal>
    </div>
    <Reveal delay={0.15} className={flip ? "lg:order-1" : ""}>
      <ul className="rounded-3xl border border-slate-200 bg-white p-6 sm:p-8 space-y-1 shadow-sm">
        {bullets.map((b, i) => (
          <li
            key={b}
            data-testid={`${testid}-bullet-${i + 1}`}
            className="flex items-start gap-3 rounded-2xl px-3 py-3 transition-colors duration-300 hover:bg-brand-50"
          >
            <span className="mt-0.5 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-brand-600 text-white">
              <Check className="h-3.5 w-3.5" />
            </span>
            <span className="text-sm sm:text-base font-medium text-slate-700">{b}</span>
          </li>
        ))}
      </ul>
    </Reveal>
  </div>
);

export default function Services() {
  return (
    <>
      <Seo
        title="Meta & Google Ads Services — Chatbudds"
        description="Meta Ads and Google Ads services built to perform: strategy, creative, targeting, tracking and weekly optimisation with transparent reporting."
      />

      <section className="relative overflow-hidden" data-testid="services-hero">
        <div className="absolute inset-0 dot-grid" aria-hidden="true" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-32 pb-16 md:pt-44 md:pb-20">
          <Reveal>
            <span className="inline-flex items-center rounded-full border border-brand-200 bg-brand-50 px-3 py-1 text-xs font-semibold uppercase tracking-widest text-brand-700" data-testid="services-eyebrow">
              Services
            </span>
          </Reveal>
          <Reveal delay={0.1}>
            <h1 className="mt-6 max-w-3xl font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-slate-900 leading-[1.06]">
              Ad services built to <span className="text-brand-600">perform</span>.
            </h1>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-6 max-w-2xl text-base md:text-lg leading-relaxed text-slate-600">
              We specialise in the two platforms where your customers spend their time and make their decisions.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16 md:pb-24">
        <ServiceBlock
          icons={[Facebook, Instagram]}
          title="Meta Ads (Facebook and Instagram)"
          intro="Creative-led campaigns on Facebook and Instagram, built to generate leads and sales — not just likes."
          bullets={metaAds}
          cta="Start with Meta Ads"
          testid="meta-ads-service"
          ctaTestid="meta-ads-service-cta-button"
        />
      </section>

      <section className="bg-slate-50/70">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
          <ServiceBlock
            icons={[Search, Youtube]}
            title="Google Ads"
            intro="Capture people who are already searching for what you sell, across Search, Display, YouTube and Performance Max."
            bullets={googleAds}
            cta="Start with Google Ads"
            testid="google-ads-service"
            ctaTestid="google-ads-service-cta-button"
            flip
          />
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24" data-testid="process-section">
        <Reveal>
          <span className="inline-flex items-center rounded-full border border-brand-200 bg-brand-50 px-3 py-1 text-xs font-semibold uppercase tracking-widest text-brand-700">
            Our process
          </span>
        </Reveal>
        <Reveal delay={0.1}>
          <h2 className="mt-4 max-w-2xl font-display text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">
            Five steps. Repeated relentlessly.
          </h2>
        </Reveal>
        <div className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
          {process.map((p, i) => (
            <Reveal key={p.title} delay={i * 0.1}>
              <div
                data-testid={`process-step-${i + 1}`}
                className="relative h-full rounded-3xl border border-slate-200 bg-white p-6 text-center transition-all duration-300 hover:border-brand-300 hover:shadow-lg hover:shadow-brand-900/5"
              >
                <span className="mx-auto inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-brand-600 text-white">
                  <p.icon className="h-5 w-5" />
                </span>
                <p className="mt-4 font-display text-base font-bold text-slate-900">{p.title}</p>
                <span className="absolute right-4 top-4 font-display text-xs font-bold text-brand-200">
                  {String(i + 1).padStart(2, "0")}
                </span>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <CTABanner
        title="Not sure which platform fits your business?"
        buttonLabel="Get Free Advice"
        testid="services-final-cta-button"
      />
    </>
  );
}
